import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'clsx';
import { get } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox/';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { v4 } from 'uuid';

import {
  MULTI_PACKAGE_ADD_PACKAGE_CLICKED,
  MULTI_PACKAGE_APPLY_PACKAGE_SET_CLICKED,
  MULTI_PACKAGE_DUPLICATE_CLICKED,
  MULTI_PACKAGE_DELETE_CLICKED,
  MULTI_PACKAGE_SAVE_WITH_PACKAGE_SET,
  MULTI_PACKAGE_SAVE_PACKAGES_CLICKED,
} from '../../../constants/analytics-events';
import { PackageItem } from './package-item/';
import { PackageSetsList } from './package-set-list';
import { Segment } from '../../../services/analytics-services/segment';
import { setupV2PackageSet, setupV3PackageSet } from './setup-package-set';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';
import { useSavePackageSet } from './hooks/use-save-package-set';

import style from './multi-package-modal.module.less';
import { messages } from './messages';
import { ErrorBoundary } from '../../../components/error-boundary';
import { FormattedCurrency } from '../../../components/formatted-currency';
import { emptyArray } from 'ui-components/default-props/empty';
import { buildPackageWithPreferredUnits, getDefaultPackage } from '../../../services/default-package-service';
import { IntlUnitFormattingService } from '../../../services/intl-unit-formatting-services/intl-unit-formatting-service';
import { defaultGenericPackage } from '../../../services/package-service';
import { convertToDimensionUnitEnum } from '../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { convertPackageWeightAndDimensions } from '../../../services/unit-services/package-services/package-unit-conversion-service';
import { convertAndSumWeightValues } from '../../../services/unit-services/weights/weight-convert-and-sum-service';
import Global from '../../../store/global-selectors';

export const MULTI_PACKAGE_MODAL_CONTEXTS = {
  Orders: 'orders',
  Settings: 'settings',
};

const DEFAULT_PACKAGE_SETS_FIVE = 5;
const DEFAULT_PACKAGE_SETS_TEN = 10;
const DEFAULT_PACKAGE_SETS_FIFTEEN = 15;

const PACKAGE_QUANTITY_OPTIONS = [DEFAULT_PACKAGE_SETS_FIVE, DEFAULT_PACKAGE_SETS_TEN, DEFAULT_PACKAGE_SETS_FIFTEEN];

const ContextSpecificValues = {
  [MULTI_PACKAGE_MODAL_CONTEXTS.Orders]: {
    modalHeaderTitle: messages.multiPackageTitle,
    savePackagesButtonText: messages.savePackagesButton,
    leftPaneClassName: style['left-pane'],
    modalFooterClassName: style.footer,
  },
  [MULTI_PACKAGE_MODAL_CONTEXTS.Settings]: {
    modalHeaderTitle: messages.multiPackageSettingsTitle,
    savePackagesButtonText: messages.saveNewPackageSetButton,
    leftPaneClassName: style['left-pane-for-settings'],
    modalFooterClassName: style['footer-for-settings'],
  },
};

const addInitialPackageIds = (pkg) => {
  if (pkg?.packageTypeId) {
    return pkg;
  }
  return {
    ...pkg,
    packageTypeId: defaultGenericPackage.packageTypeId,
    packageId: defaultGenericPackage.packageId,
  };
};

const createInitialPackages = (
  initialPackages,
  sellerDefaultCurrencyCode,
  sellerDefaultDimensionsUnit,
  sellerDefaultWeightUnit,
  hasInsuranceProviderSelected,
) => {
  const defaultPackageCurrencyCode = initialPackages[0]?.insuredValue?.code || sellerDefaultCurrencyCode;
  const packageToAdd = buildPackageWithPreferredUnits(
    addInitialPackageIds(initialPackages[0]),
    sellerDefaultWeightUnit,
    sellerDefaultDimensionsUnit,
    defaultPackageCurrencyCode,
  );
  const calculatedPackages = (initialPackages.length > 1 ? initialPackages : [packageToAdd]).map((p) => {
    return {
      ...convertPackageWeightAndDimensions(p, sellerDefaultWeightUnit, sellerDefaultDimensionsUnit),
      insuredValue: hasInsuranceProviderSelected ? p.insuredValue : { value: 0, code: defaultPackageCurrencyCode },
      contentDescription: p.contentDescription,
      id: v4(),
    };
  });

  return calculatedPackages;
};

const updatePackage = (setPackages, updateObj, index) => {
  setPackages((packages) => {
    const updatedPackage = {
      ...packages[index],
      ...updateObj,
    };
    const copiedPackages = [...packages];
    copiedPackages[index] = updatedPackage;
    return copiedPackages;
  });
};

/**
 * @typedef {import('../../../prop-types/package').PackagePropType} PackagePropType
 */

/**
 * @param {object} props
 * @param {boolean} [props.hasInsuranceProviderSelected]
 * @param {PackagePropType[]} [props.initialPackages]
 * @param {(packages: PackagePropType[]) => void} [props.onSave]
 * @param {boolean} [props.isReadOnly]
 * @param {boolean} [props.shouldRequirePackageDescription]
 * @param {boolean} [props.hasMultiPackageSupport]
 * @param {string} [props.context]
 * @returns {JSX.Element}
 */
export const MultiPackageModal = ({
  hasInsuranceProviderSelected = false,
  initialPackages = emptyArray,
  onSave,
  isReadOnly = false,
  shouldRequirePackageDescription = false,
  hasMultiPackageSupport = false,
  context = MULTI_PACKAGE_MODAL_CONTEXTS.Orders,
}) => {
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const packageSetsList = useSelector((state) => Global.selectSellerPackageSets(state)) || [];
  const enableMultiPackageShipments = useMultiPackageShipments();
  const { formatMessage } = useIntl();
  const hasPackageSets = packageSetsList.length > 0;

  const isOrdersContext = context === MULTI_PACKAGE_MODAL_CONTEXTS.Orders;
  const isSettingsContext = context === MULTI_PACKAGE_MODAL_CONTEXTS.Settings;

  const modalClasses = classNames(style.modal, {
    [style['read-only']]: isReadOnly,
  });
  const shouldShowMultiPackageNotSupportedBanner = isOrdersContext && enableMultiPackageShipments && !hasMultiPackageSupport;
  const shouldShowPackageSetsList = isOrdersContext && hasPackageSets;

  const { modalHeaderTitle, savePackagesButtonText, leftPaneClassName, modalFooterClassName } = ContextSpecificValues[context];

  const {
    onPackageSetNameChange,
    onSavePackageSet,
    packageSetName,
    shouldSaveNewPackageSet,
    toggleShouldSaveNewPackageSet,
    setShouldSaveNewPackageSet,
  } = useSavePackageSet();

  const [packages, setPackages] = useState(() =>
    createInitialPackages(
      initialPackages,
      sellerDefaultCurrencyCode,
      sellerDefaultDimensionsUnit,
      sellerDefaultWeightUnit,
      hasInsuranceProviderSelected,
    ),
  );

  const packagesCount = packages.length;
  const totalPackageSetWeight = IntlUnitFormattingService.formatWeight(
    convertAndSumWeightValues(
      packages.map((p) => p.weight),
      sellerDefaultWeightUnit,
    ),
    sellerDefaultWeightUnit,
  );

  const multiPackageInsuranceTotal = useMemo(
    () =>
      packages.reduce(
        (sum, p) => {
          const value = sum.value + get(p, 'insuredValue.value', 0);
          const code = sum.code || get(p, 'insuredValue.code', sellerDefaultCurrencyCode);
          return {
            value,
            code,
          };
        },
        { value: 0, code: sellerDefaultCurrencyCode },
      ),
    [packages, sellerDefaultCurrencyCode],
  );

  const duplicatePackage = useCallback(
    (packageToDuplicate) => {
      Segment.trackEvent(MULTI_PACKAGE_DUPLICATE_CLICKED, {
        type: MULTI_PACKAGE_DUPLICATE_CLICKED,
        packageCount: packages.length,
      });
      const { orderPackageId: _orderPackageId, ...packageWithoutId } = packageToDuplicate;
      setPackages([
        ...packages,
        {
          ...packageWithoutId,
          id: v4(),
        },
      ]);
    },
    [packages],
  );

  const removePackage = useCallback(
    (packageSetItemIndex) => {
      Segment.trackEvent(MULTI_PACKAGE_DELETE_CLICKED, {
        type: MULTI_PACKAGE_DELETE_CLICKED,
        packageCount: packages.length,
      });

      if (packages.length === 1) {
        const defaultPackage = getDefaultPackage({
          weightUnit: sellerDefaultWeightUnit,
          dimensionsUnit: sellerDefaultDimensionsUnit,
          currencyCode: sellerDefaultCurrencyCode,
        });
        defaultPackage.id = v4();
        setPackages([defaultPackage]);
      } else {
        setPackages(packages.filter((_, index) => index !== packageSetItemIndex));
      }
    },
    [packages, sellerDefaultCurrencyCode, sellerDefaultDimensionsUnit, sellerDefaultWeightUnit],
  );

  const addPackages = useCallback(
    (qtyToAdd) => {
      Segment.trackEvent(MULTI_PACKAGE_ADD_PACKAGE_CLICKED, {
        type: MULTI_PACKAGE_ADD_PACKAGE_CLICKED,
        packagesAddedCount: qtyToAdd,
      });

      const packagesCurrency = packages[0]?.insuredValue?.code || sellerDefaultCurrencyCode;
      const defaultPackage = getDefaultPackage({
        weightUnit: sellerDefaultWeightUnit,
        dimensionsUnit: sellerDefaultDimensionsUnit,
        currencyCode: packagesCurrency,
      });
      const newPackages = Array.from({ length: qtyToAdd }, () => ({
        ...defaultPackage,
        id: v4(),
      }));
      setPackages([...packages, ...newPackages]);
    },
    [packages, sellerDefaultCurrencyCode, sellerDefaultDimensionsUnit, sellerDefaultWeightUnit],
  );

  const changePackageDimensions = useCallback((inputDimensions, index) => {
    const unit = convertToDimensionUnitEnum(inputDimensions.unit);
    const dimensions = {
      length: inputDimensions.length,
      width: inputDimensions.width,
      height: inputDimensions.height,
      unit,
    };
    updatePackage(setPackages, { dimensions }, index);
  }, []);

  const changeWeight = useCallback((weightAndUnit, index) => {
    updatePackage(setPackages, { weight: weightAndUnit }, index);
  }, []);

  const changePackageContentDescription = useCallback((updatedDescription, index) => {
    updatePackage(setPackages, { contentDescription: updatedDescription }, index);
  }, []);

  const changeInsuranceValue = useCallback((insuredAmount, currencyCode, index) => {
    const parsedValue = Number.parseFloat(insuredAmount);
    const actualValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
    const insuredValue = {
      value: actualValue,
      code: currencyCode,
    };
    updatePackage(setPackages, { insuredValue }, index);
  }, []);

  const applyPackageSetFromList = useCallback(
    (packageSet) => {
      Segment.trackEvent(MULTI_PACKAGE_APPLY_PACKAGE_SET_CLICKED, {
        type: MULTI_PACKAGE_APPLY_PACKAGE_SET_CLICKED,
        packageSetName: packageSet.name,
      });

      const parsedData = JSON.parse(get(packageSet, 'jsonData'));
      let packagesWithUnits;
      if (Array.isArray(parsedData)) {
        packagesWithUnits = setupV2PackageSet(parsedData, sellerDefaultDimensionsUnit);
      } else {
        packagesWithUnits = setupV3PackageSet(parsedData, sellerDefaultDimensionsUnit);
      }
      setPackages(packagesWithUnits);
    },
    [sellerDefaultDimensionsUnit],
  );

  const onSaveClicked = useCallback(() => {
    const parsedPackages = packages.map((pack) => {
      const newPack = { ...pack };
      delete newPack.id;
      return newPack;
    });
    if (shouldSaveNewPackageSet) {
      Segment.trackEvent(MULTI_PACKAGE_SAVE_WITH_PACKAGE_SET, {
        type: MULTI_PACKAGE_SAVE_WITH_PACKAGE_SET,
        context,
        packageSetName,
      });
    } else {
      Segment.trackEvent(MULTI_PACKAGE_SAVE_PACKAGES_CLICKED, {
        type: MULTI_PACKAGE_SAVE_PACKAGES_CLICKED,
        context,
      });
    }
    if (onSave) {
      onSave(parsedPackages);
    }
    onSavePackageSet(parsedPackages);
  }, [context, onSave, onSavePackageSet, packageSetName, packages, shouldSaveNewPackageSet]);

  const handlePackageSetNameChange = (e) => {
    setShouldSaveNewPackageSet?.(true);
    onPackageSetNameChange(e);
  };

  return (
    <ErrorBoundary errorMessageId='multiPackageModal'>
      <Modal className={modalClasses}>
        <ModalHeader>
          <FormattedMessage {...modalHeaderTitle} />
        </ModalHeader>
        <ModalBody className={style.body}>
          <div className={leftPaneClassName}>
            <div className={style['sub-header']}>
              {isSettingsContext ? (
                <Input
                  className={style['package-set-name-input']}
                  label={<Text textType={TextType.Label} value={messages.packageSetNameLabel} />}
                  onChange={handlePackageSetNameChange}
                  version={version2}
                />
              ) : null}
              <div className={style['button-group']}>
                <Button
                  onClick={() => {
                    addPackages(1);
                  }}
                  theme='primary'
                >
                  <FormattedMessage {...messages.addPackage} />
                </Button>
                <DropdownMenu className={style['add-package-dropdown']}>
                  <DropdownMenuToggler className={style['dropdown-button']} theme='primary' />
                  {PACKAGE_QUANTITY_OPTIONS.map((packageOption) => (
                    <DropdownMenuItem key={packageOption} onClick={() => addPackages(packageOption)}>
                      <FormattedMessage {...messages.addPackagesLabel} values={{ numberOfPackages: packageOption }} />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </div>
              {shouldShowPackageSetsList ? (
                <PackageSetsList
                  applyPackageSetFromList={applyPackageSetFromList}
                  packageSetsList={packageSetsList}
                  packageSetsListClassName={style['package-sets-dropdown']}
                />
              ) : null}
            </div>
            <div className={style['package-set']}>
              {shouldShowMultiPackageNotSupportedBanner ? (
                <FlexRow className={style['multi-package-not-supported-banner']}>
                  <BannerMessageV2
                    content={<Text textType={TextType.Caption} value={messages.multiPackageNotSupported} />}
                    status={BannerStatus.Warning}
                  />
                </FlexRow>
              ) : null}
              {packages.map((p, i) => (
                <div key={p.id} className={style['package-container']}>
                  <PackageItem
                    changeInsuranceValue={changeInsuranceValue}
                    changePackageContentDescription={changePackageContentDescription}
                    changePackageDimensions={changePackageDimensions}
                    changeWeight={changeWeight}
                    duplicatePackage={duplicatePackage}
                    hasInsuranceProviderSelected={isSettingsContext || hasInsuranceProviderSelected}
                    index={i}
                    pack={p}
                    readOnly={isReadOnly}
                    removePackage={removePackage}
                    shouldRequirePackageDescription={shouldRequirePackageDescription}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={style['right-pane']}>
            <div className={style['package-count']}>
              <div className={style.badge}>{packagesCount}</div>
              <FormattedMessage {...messages.sidePanelPackagesLabel} />
            </div>
            <div className={style['package-set-details']}>
              <div className={style['details-weight']}>
                <span className={style['details-label']}>
                  <FormattedMessage {...messages.sidePanelTotalWeightLabel} />
                </span>
                {totalPackageSetWeight}
              </div>
              <div>
                <span className={style['details-label']}>
                  <FormattedMessage {...messages.sidePanelTotalInsuranceLabel} />
                </span>
                <FormattedCurrency currencyCode={multiPackageInsuranceTotal.code} value={multiPackageInsuranceTotal.value} />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={modalFooterClassName}>
          {isOrdersContext ? (
            <div className={style['new-packages']}>
              <Checkbox
                checked={shouldSaveNewPackageSet}
                label={formatMessage(messages.saveNewPackageSet)}
                onChange={toggleShouldSaveNewPackageSet}
              />
              {shouldSaveNewPackageSet ? (
                <input className={style['package-set-name']} onChange={onPackageSetNameChange} type='text' value={packageSetName} />
              ) : null}
            </div>
          ) : null}
          <div className={style['footer-buttons']}>
            <ModalFooterCancelButton />
            <div className={style.spacer} />
            <Button onClick={onSaveClicked} theme='primary'>
              <FormattedMessage {...savePackagesButtonText} />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </ErrorBoundary>
  );
};
